/**
 * The wind triangle — the E6B computation at the heart of every navlog leg.
 *
 * Conventions follow the LBA Flugdurchführungsplan (fsm 1/87):
 *   rwK / TC   true course
 *   WCA        wind correction angle, positive = correct to the right
 *   MW / VAR   magnetic variation, signed: west negative (as written "-2"), east positive
 *   mwSK / MH  magnetic heading  = TC + WCA − VAR
 *   mwK / MC   magnetic course   = TC − VAR
 *   VG / GS    ground speed
 */
import { norm360 } from './parse.js';
const RAD = Math.PI / 180;
/**
 * Solve the wind triangle. Returns null when the leg is not flyable —
 * the crosswind component meets or exceeds TAS, or the resulting ground
 * speed would be zero or negative. Callers should surface that as a warning,
 * never as a number.
 */
export function windTriangle({ tas, wind, tc }) {
    if (!(tas > 0))
        return null;
    const rel = (wind.dir - tc) * RAD;
    const crosswind = wind.spd * Math.sin(rel); // + from the right
    const headwind = wind.spd * Math.cos(rel); // + headwind
    if (Math.abs(crosswind) >= tas)
        return null;
    const wcaRad = Math.asin(crosswind / tas);
    const gs = tas * Math.cos(wcaRad) - headwind;
    if (gs <= 0)
        return null;
    return { wca: wcaRad / RAD, gs, headwind, crosswind };
}
/** mwSK: magnetic heading from true course, WCA and signed variation. */
export function magneticHeading(tc, wca, variation) {
    return norm360(tc + wca - variation);
}
/** mwK: magnetic course from true course and signed variation. */
export function magneticCourse(tc, variation) {
    return norm360(tc - variation);
}
/** Leg time in whole minutes (minimum 1) from distance in NM and ground speed in knots. */
export function legMinutes(distanceNm, gs) {
    if (!(distanceNm >= 0) || !(gs > 0))
        return null;
    return Math.max(1, Math.round((distanceNm / gs) * 60));
}
//# sourceMappingURL=wind.js.map